/** 退货单详情字段展示辅助 */

import { RETURN_ORDER_STATUS_LABELS } from '@/temu-platform/constants/temuApiCatalog';
import { pickField } from '@/temu-platform/utils/temuResponse';

export const NO_RETURN_SIGN_LABELS = {
  0: '是',
  1: '否',
};

export const RETURN_ORDER_TAG_LABELS = {
  1: 'KOL退货单',
  2: '丢件',
};

export const INVOICE_STATUS_LABELS = {
  0: '不开票',
  1: '待开票',
  2: '已申请发票',
  3: '开票成功',
};

export const CHECK_STATUS_LABELS = {
  1: '已对账',
  2: '未对账',
};

export const STOCK_MODE_LABELS = {
  1: 'TEMU自主备货',
  2: '备货到TEMU仓',
  3: '不备货到TEMU仓',
};

export const RECEIVE_TYPE_LABELS = {
  0: '默认',
  1: '卖家收货',
  2: '仓库收货',
};

export function normalizeImageUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('//')) return `https:${raw}`;
  return raw;
}

export function mapLabel(map, value) {
  if (value === undefined || value === null || value === '') return '';
  const key = String(value);
  if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  if (Object.prototype.hasOwnProperty.call(map, Number(value))) return map[Number(value)];
  return String(value);
}

export function formatMoney(currency, amount) {
  if (amount === undefined || amount === null || amount === '') return '';
  const cur = currency ? `${currency} ` : '';
  return `${cur}${amount}`;
}

export function pickAttrName(attrs) {
  if (!Array.isArray(attrs) || !attrs.length) return '';
  const cn = attrs.find((a) => String(a?.language || '').toUpperCase() === 'CN');
  const en = attrs.find((a) => {
    const lang = String(a?.language || '').toUpperCase();
    return lang === 'EN' || lang === 'US';
  });
  return cn?.attrName || en?.attrName || attrs[0]?.attrName || '';
}

export function pickReturnReason(reasons) {
  if (!Array.isArray(reasons) || !reasons.length) return '';
  const cn = reasons.find((r) => String(r?.language || '').toUpperCase() === 'CN');
  const en = reasons.find((r) => String(r?.language || '').toUpperCase() === 'EN');
  return cn?.reason || en?.reason || reasons[0]?.reason || '';
}

export function statusLabelOf(code) {
  return RETURN_ORDER_STATUS_LABELS[code] || (code === '' || code == null ? '' : String(code));
}

/**
 * 从退货单详情解析签收所需 goodsIdList（平台要求 integer/long[]）。
 * 超出 JS 安全整数时保留十进制字符串，后端 Long 解析。
 */
export function extractGoodsIdList(detail) {
  const goods = Array.isArray(detail?.returnGoodsInfoList) ? detail.returnGoodsInfoList : [];
  const out = [];
  for (const g of goods) {
    const raw = g?.goodsId;
    if (raw === undefined || raw === null || String(raw).trim() === '') continue;
    if (typeof raw === 'number' && Number.isSafeInteger(raw) && raw > 0) {
      out.push(raw);
      continue;
    }
    const text = String(raw).trim();
    if (!/^\d+$/.test(text)) continue;
    const n = Number(text);
    out.push(Number.isSafeInteger(n) && n > 0 ? n : text);
  }
  return out;
}

export function displayValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join('、');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** 列表行商品摘要 */
export function listProductSummary(row) {
  const title = pickField(row, 'goodsTitle');
  const imageUrl = normalizeImageUrl(pickField(row, 'imageUrl'));
  const skc = pickField(row, 'skc');
  // 列表展示商家 SKU（skuSn），不是平台 skuId
  const merchantSku = pickField(row, 'skuSn');
  const currency = pickField(row, 'currency');
  const refundAmount = pickField(row, 'refundAmount');
  const goodsCount = Number(row?.goodsCount) || 0;
  return {
    title,
    imageUrl,
    skc,
    merchantSku,
    currency,
    refundAmount,
    goodsCount,
    refundText: formatMoney(currency, refundAmount),
  };
}

const MONEY_SUM_KEYS = [
  'sellerCurrencyPrice',
  'costPrice',
  'sellerCurrencyStoreCouponPrice',
  'sellerCurrencyPromotionPrice',
  'settleCurrencyPromotionPrice',
  'estimateCommission',
  'commissionSaleTax',
  'sellerRealTax',
  'performancePrice',
  'returnExpense',
  'estimateIncomeMoney',
  'estimateTaxIncomeMoney',
];

function moneyFingerprint(goods) {
  return MONEY_SUM_KEYS.map((key) => String(goods?.[key] ?? '')).join('/');
}

/** 相同退货商品合并键：同 SKC/SPU/商家SKU/标题/原因/状态/单价口径则合并（SKU 可不同） */
export function returnGoodsMergeKey(goods) {
  return [
    pickField(goods, 'skc'),
    pickField(goods, 'goodsSn'),
    pickField(goods, 'skuSn'),
    pickField(goods, 'goodsTitle'),
    pickReturnReason(goods?.returnReasonList),
    pickField(goods, 'goodsStatus'),
    pickAttrName(goods?.commodityAttributeList),
    normalizeImageUrl(pickField(goods, 'imageUrl')),
    moneyFingerprint(goods),
  ].join('|');
}

function toNumber(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function sumMoneyField(items, key) {
  let total = 0;
  let hit = false;
  for (const item of items) {
    const n = toNumber(item?.[key]);
    if (n == null) continue;
    total += n;
    hit = true;
  }
  return hit ? total : '';
}

/**
 * 合并相同退货商品用于详情展示。
 * @returns {{ goods: object, quantity: number, skuIds: string[] }[]}
 */
export function mergeIdenticalReturnGoods(list) {
  if (!Array.isArray(list) || !list.length) return [];
  const groups = new Map();
  for (const goods of list) {
    if (!goods || typeof goods !== 'object') continue;
    const key = returnGoodsMergeKey(goods);
    const sku = String(pickField(goods, 'sku') || '').trim();
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        items: [goods],
        skuIds: sku ? [sku] : [],
      });
      continue;
    }
    existing.items.push(goods);
    if (sku && !existing.skuIds.includes(sku)) {
      existing.skuIds.push(sku);
    }
  }

  return Array.from(groups.values()).map((group) => {
    const base = group.items[0];
    const quantity = group.items.length;
    const merged = { ...base };
    if (quantity > 1) {
      for (const key of MONEY_SUM_KEYS) {
        const summed = sumMoneyField(group.items, key);
        if (summed !== '') merged[key] = summed;
      }
      const mediaBuckets = [];
      for (const item of group.items) {
        if (Array.isArray(item.returnImageList)) mediaBuckets.push(...item.returnImageList);
        if (Array.isArray(item.returnImages)) mediaBuckets.push(...item.returnImages);
        if (Array.isArray(item.imageList)) mediaBuckets.push(...item.imageList);
        if (Array.isArray(item.returnVideoList)) mediaBuckets.push(...item.returnVideoList);
        if (Array.isArray(item.videoList)) mediaBuckets.push(...item.videoList);
      }
      if (mediaBuckets.length) {
        const seen = new Set();
        merged.returnImageList = mediaBuckets.filter((m) => {
          const link = typeof m === 'string'
            ? m
            : String(m?.link || m?.url || m?.imageUrl || m?.videoUrl || m?.src || '');
          if (!link || seen.has(link)) return false;
          seen.add(link);
          return true;
        });
      }
    }
    if (group.skuIds.length === 1) {
      merged.sku = group.skuIds[0];
    } else if (group.skuIds.length > 1) {
      merged.sku = group.skuIds.join('、');
    }
    return {
      goods: merged,
      quantity,
      skuIds: group.skuIds,
    };
  });
}
